import { Injectable, inject } from '@angular/core';
import { HOME_APP_NAME, LoggerService, Module, ModuleType, ScopeType, User, setAppIcon } from '@enliven/angular';
import { E360_USER } from '../constants/storage-constants';

@Injectable({
  providedIn: 'root'
})
export class E360UserService {
  private readonly logger: LoggerService = inject(LoggerService);

  private _isGlobalUser: boolean = false;
  private _isMultiOrgUser: boolean = false;
  private _user: User;
  private _userFullName: string;
  private _userOrgId: string;
  private _currentOrgId: string;

  private _modules: Module[] = [];
  private _adminModules: Module[] = [];
  private _homeModule: Module = undefined;
  private _modulesForCurrentOrg: Module[] = [];
  private _modulesForDifferentOrg: Module[] = [];

  get adminModules(): Module[] {
    return this._adminModules;
  }

  get homeModule(): Module {
    return this._homeModule;
  }

  get isGlobalUser(): boolean {
    return this._isGlobalUser;
  }

  get isMultiOrgUser(): boolean {
    return this._isMultiOrgUser;
  }

  get modulesForCurrentOrg(): Module[] {
    return this._modulesForCurrentOrg;
  }

  get modulesForDifferentOrg(): Module[] {
    return this._modulesForDifferentOrg;
  }

  get user(): User {
    return this._user;
  }

  get userFullName(): string {
    return this._userFullName;
  }

  get userOrgId(): string {
    return this._userOrgId;
  }

  set userOrgId(val: string) {
    this._userOrgId = val;
  }

  get currentOrgId(): string {
    return this._currentOrgId;
  }

  set currentOrgId(val: string) {
    this._currentOrgId = val;
  }

  set modules(orgModules: Module[]) {
    orgModules?.forEach(m => setAppIcon(m));
    this._adminModules = [];
    this._modulesForCurrentOrg = [];
    this._modulesForDifferentOrg = [];
    this._homeModule = undefined;
    this._modules = this.orderMods(orgModules);
    this.buildViewModuleLists();
  }

  get modules(): Module[] {
    return this._modules;
  }

  set user(user: User) {
    sessionStorage.setItem(E360_USER, JSON.stringify(user));
    this._user = user;
    this._userFullName = `${user.firstName} ${user.lastName}`;
    this._isMultiOrgUser = this._user?.scopeRoles?.[0].scope?.value?.organizations?.length > 1;
    this._isGlobalUser = this._user?.scopeRoles?.[0].scope?.type === ScopeType.GLOBAL;
    this.modules = user.modules;
    this._userOrgId = user.organizationId;
  }

  getDefaultModule(moduleIdInParams: string, organizationDefaultModuleId: string): Module {
    const urlModuleId: string = moduleIdInParams;
    const urlModule: Module = this._modules.find(m => m.id === urlModuleId);

    if (urlModule) return urlModule;

    if (urlModuleId && !urlModule) {
      this.logger.error('Module in URL is either incorrect or user does not have access to it. Module ID: %s', {
        moduleId: urlModuleId
      });
    }

    return organizationDefaultModuleId ? this._modules.find(m => m.id === organizationDefaultModuleId) : null;
  }

  private buildViewModuleLists() {
    this._modules.forEach(m => {
      if (m.type === ModuleType.API) return;

      if (m.management) {
        this._adminModules.push(m);
        return;
      }

      if (m.name === HOME_APP_NAME) {
        this._homeModule = m;
      }

      const orgId = this._isMultiOrgUser ? this._currentOrgId : this._user.organizationId;
      if (m.organizationId === orgId) {
        this._modulesForCurrentOrg.push(m);
      } else {
        this._modulesForDifferentOrg.push(m);
      }
    });
  }

  private orderMods(mods: Module[]): Module[] {
    const alphabeticalMods: Module[] = mods.sort((a, b) => a.displayName.localeCompare(b.displayName));
    const homeModIndex: number = mods.findIndex(m => m.name === HOME_APP_NAME);
    if (homeModIndex > -1) {
      mods.unshift(mods.splice(homeModIndex, 1)[0]);
    }
    const topLevelMods: Module[] = alphabeticalMods.filter(m => m.type === ModuleType.TOP);
    const embeddedMods: Module[] = alphabeticalMods.filter(m => m.type === ModuleType.EMBEDDED);
    return [...embeddedMods, ...topLevelMods];
  }
}
